import { useState, useMemo } from "react";
import { ChevronUp, ChevronDown, ChevronsUpDown } from "lucide-react";

// Generic client-side sorting for list tables.
// accessors: { columnKey: (row) => value }  — define OUTSIDE the component so the memo stays stable.
export function useSort(rows, accessors, initialKey = null, initialDir = "asc") {
  const [sort, setSort] = useState(initialKey ? { key: initialKey, dir: initialDir } : null);
  const toggle = (key) =>
    setSort((s) => (s && s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));
  const sorted = useMemo(() => {
    if (!sort || !accessors[sort.key]) return rows;
    const acc = accessors[sort.key];
    return [...rows].sort((a, b) => {
      const va = acc(a), vb = acc(b);
      let cmp;
      if (typeof va === "number" && typeof vb === "number") cmp = (va || 0) - (vb || 0);
      else cmp = String(va ?? "").localeCompare(String(vb ?? ""), undefined, { numeric: true });
      return sort.dir === "asc" ? cmp : -cmp;
    });
  }, [rows, sort, accessors]);
  return { sorted, sort, toggle };
}

// Renders inside your existing <th>. Pass the same classes you'd use on the label.
export function SortBtn({ label, col, sort, onSort, className = "" }) {
  const active = sort?.key === col;
  return (
    <button
      type="button"
      data-testid={`sort-${col}`}
      onClick={() => onSort(col)}
      className={`inline-flex items-center gap-1 hover:text-black transition-colors ${active ? "text-black" : ""} ${className}`}
    >
      {label}
      {active ? (
        sort.dir === "asc" ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />
      ) : (
        <ChevronsUpDown className="h-3 w-3 opacity-40" />
      )}
    </button>
  );
}
